import { type ApplicationName, CREDENTIAL_TYPES, resolveApplicationName } from '@access-cms/shared-constants';

import type {
  ApprovalDetailsAdditionalAccessSection,
  ApprovalDetailsSection,
  DocumentFile,
  DocumentGroup,
  PivCardSelection,
} from './types';
import { DocumentFieldName, DocumentGroupId, SectionId } from './constants';

type CredentialType = PivCardSelection | 'external_nonfederal_piv';

interface UserData {
  dateOfBirth?: string | null;
  email?: string;
  firstName?: string | null;
  lastName?: string | null;
  middleName?: string | null;
  phoneNumber?: string | null;
  ssnLast4?: string | null;
  suffix?: string | null;
  userType?: string;
}

interface AddressData {
  city?: string;
  state?: string;
  streetLine1?: string;
  streetLine2?: string;
  zipCode?: string;
}

interface OtherNameData {
  firstName?: string;
  lastName?: string;
  middleName?: string;
  suffix?: string;
}

interface PersonalDetails extends ApplicationName {
  hasExternalFederalPiv?: boolean;
  noMiddleName?: boolean;
  otherNamesUsed?: OtherNameData[];
  personalAddress?: AddressData;
  personalPhoneNumber?: string;
  workFromHome?: boolean;
}

interface CitizenshipDetails {
  birthCity?: string;
  birthCountry?: string;
  birthState?: string;
  countryOfCitizenship?: string;
  entryVisaType?: string;
  firstEntryDate?: string;
  immigrationData?: {
    immigrationDate?: string;
    visaNumber?: string;
  }[];
  isPermanentResident?: boolean;
  isUsCitizen?: boolean;
  sex?: string;
  yearsInUSPast5Years?: string;
  passport?: {
    expirationDate?: string;
    issuanceCountry?: string;
    issueDate?: string;
    number?: string;
    type?: string;
  };
}

interface ContractDetails {
  accessJustification?: string;
  cmsRegionFacility?: string;
  seasonal?: boolean;
  workAddress?: AddressData;
  workEmail?: string;
  workExtension?: string;
  workPhoneNumber?: string;
}

interface CorApproval {
  credentialType?: CredentialType;
  physicalAccess?: 'yes' | 'no';
}

interface DocumentRef {
  filename?: string;
  fileSize?: number;
  fileUrl?: string;
  id: string;
  mimeType?: string;
  s3Key?: string;
  uploadedAt?: string;
}

interface Documents {
  legalDocuments?: DocumentRef[];
  otherDocuments?: DocumentRef[];
  passport?: DocumentRef;
  pivTrainingCertificate?: DocumentRef;
  residencyDocument?: DocumentRef;
  residencySupportingDocuments?: DocumentRef[];
  resume?: DocumentRef;
  usVisaDocument?: DocumentRef;
}

export interface ApplicationData {
  citizenshipDetails?: CitizenshipDetails;
  contractDetails?: ContractDetails;
  corApproval?: CorApproval;
  documents?: Documents;
  hasExternalFederalPiv?: boolean;
  personalDetails?: PersonalDetails;
}

export interface ApplicationWithUser {
  applicationData?: ApplicationData;
  contract?: {
    contractNumber?: string;
    companyName?: string;
    hasTemporarySeasonalUsers?: boolean;
  };
  laborCategory?: {
    categoryName?: string;
  };
  user?: UserData;
}

function boolToYesNo(value?: boolean | null): string {
  if (value === true) return 'Yes';
  if (value === false) return 'No';

  return 'N/A';
}

function formatDate(isoDate?: string | null): string {
  if (!isoDate) return 'N/A';
  const parts = isoDate.split('-');
  if (parts.length === 3) {
    return `${parts[1]}/${parts[2]}/${parts[0]}`;
  }

  return isoDate;
}

function formatFileSize(bytes?: number | null): string {
  if (!bytes) return '';
  if (bytes >= 1_000_000) return `${(bytes / 1_000_000).toFixed(1)} MB`;
  if (bytes >= 1_000) return `${Math.round(bytes / 1_000)} KB`;

  return `${bytes} B`;
}

function yearsInUsLabel(value?: string | null): string {
  const map: Record<string, string> = {
    FIVE_PLUS: '5 years',
    FOUR_TO_FIVE: '4–5 years',
    LESS_THAN_1: 'Less than 1 year',
    ONE_TO_TWO: '1–2 years',
    THREE_TO_FOUR: '3–4 years',
    TWO_TO_THREE: '2–3 years',
  };

  return value ? map[value] ?? value : 'N/A';
}

function sexLabel(value?: string | null): string {
  if (value === 'MALE') return 'Male';
  if (value === 'FEMALE') return 'Female';

  return value ?? 'N/A';
}

function buildPersonalDetailsSection(user?: UserData, personalDetails?: PersonalDetails): ApprovalDetailsSection {
  const names = resolveApplicationName(personalDetails, user);
  const otherNames = personalDetails?.otherNamesUsed
    ?.map((n) => [n.firstName, n.middleName, n.lastName, n.suffix].filter(Boolean).join(' '))
    .join(', ');

  const addr = personalDetails?.personalAddress;

  return {
    id: SectionId.PERSONAL_DETAILS,
    rows: [
      {
        fieldPath: 'personalDetails.firstName',
        id: 'legal-first-name',
        label: 'Legal First Name',
        value: names.firstName || 'N/A',
      },
      {
        fieldPath: 'personalDetails.middleName',
        id: 'legal-middle-name',
        label: 'Legal Middle Name',
        value: names.middleName || 'N/A',
      },
      {
        fieldPath: 'personalDetails.lastName',
        id: 'legal-last-name',
        label: 'Legal Last Name',
        value: names.lastName || 'N/A',
      },
      { fieldPath: 'personalDetails.suffix', id: 'suffix', label: 'Suffix', value: user?.suffix ?? 'N/A' },
      {
        fieldPath: 'personalDetails.otherNamesUsed',
        id: 'other-names-used',
        label: 'Other Names Used',
        value: otherNames || 'N/A',
      },
      {
        fieldPath: 'personalDetails.dateOfBirth',
        id: 'date-of-birth',
        label: 'Date of Birth',
        value: formatDate(user?.dateOfBirth),
      },
      {
        fieldPath: 'personalDetails.ssn',
        id: 'social-security-number',
        label: 'Social Security Number',
        value: user?.ssnLast4 ? `***-**-${user.ssnLast4}` : '***-**-****',
      },
      {
        fieldPath: 'personalDetails.personalAddress.streetLine1',
        id: 'personal-street-address-line-1',
        label: 'Personal Street Address Line 1',
        value: addr?.streetLine1 ?? 'N/A',
      },
      {
        fieldPath: 'personalDetails.personalAddress.streetLine2',
        id: 'personal-street-address-line-2',
        label: 'Personal Street Address Line 2',
        value: addr?.streetLine2 ?? 'N/A',
      },
      {
        fieldPath: 'personalDetails.personalAddress.city',
        id: 'personal-address-city',
        label: 'Personal Address City',
        value: addr?.city ?? 'N/A',
      },
      {
        fieldPath: 'personalDetails.personalAddress.state',
        id: 'personal-address-state',
        label: 'Personal Address State',
        value: addr?.state ?? 'N/A',
      },
      {
        fieldPath: 'personalDetails.personalAddress.zipCode',
        id: 'personal-address-zip-code',
        label: 'Personal Address Zip Code',
        value: addr?.zipCode ?? 'N/A',
      },
      {
        fieldPath: 'personalDetails.workFromHome',
        id: 'do-you-work-from-home',
        label: 'Do you work from home?',
        value: boolToYesNo(personalDetails?.workFromHome),
      },
      {
        fieldPath: 'personalDetails.registrationEmail',
        id: 'registration-email',
        label: 'Registration Email',
        value: user?.email ?? 'N/A',
      },
      {
        fieldPath: 'personalDetails.personalPhoneNumber',
        id: 'personal-phone-number',
        label: 'Personal Phone Number',
        value: personalDetails?.personalPhoneNumber ?? 'N/A',
      },
      {
        fieldPath: 'personalDetails.userCategory',
        id: 'user-category',
        label: 'User Category',
        value: user?.userType ? user.userType.charAt(0).toUpperCase() + user.userType.slice(1).toLowerCase() : 'N/A',
      },
    ],
    title: 'My Personal Details',
  };
}

function buildCitizenshipSection(
  citizenship?: CitizenshipDetails,
  isForeignNational?: boolean,
): ApprovalDetailsSection {
  const immigration = citizenship?.immigrationData?.[0];
  const isPermanentResident = citizenship?.isPermanentResident === true;

  return {
    id: SectionId.CITIZENSHIP_DETAILS,
    rows: [
      {
        fieldPath: 'citizenshipDetails.isUsCitizen',
        id: 'is-us-citizen',
        label: 'Are you a United States (U.S.) Citizen?',
        value: boolToYesNo(citizenship?.isUsCitizen),
      },
      { fieldPath: 'citizenshipDetails.sex', id: 'sex', label: 'Sex', value: sexLabel(citizenship?.sex) },
      {
        fieldPath: 'citizenshipDetails.birthCountry',
        id: 'birth-country',
        label: 'Birth Country',
        value: citizenship?.birthCountry ?? 'N/A',
      },
      {
        fieldPath: 'citizenshipDetails.birthCity',
        id: 'birth-city',
        label: 'Birth City',
        value: citizenship?.birthCity ?? 'N/A',
      },
      {
        fieldPath: 'citizenshipDetails.birthState',
        id: 'birth-state-province',
        label: 'Birth State/Province',
        value: citizenship?.birthState ?? 'N/A',
      },
      ...(isForeignNational
        ? [
            {
              fieldPath: 'citizenshipDetails.isPermanentResident',
              id: 'is-permanent-resident',
              label: 'Are you a Permanent Resident of the United States (U.S.)?',
              value: boolToYesNo(citizenship?.isPermanentResident),
            },
            {
              fieldPath: 'citizenshipDetails.countryOfCitizenship',
              id: 'country-of-citizenship',
              label: 'Country of Citizenship',
              value: citizenship?.countryOfCitizenship ?? 'N/A',
            },
            ...(!isPermanentResident
              ? [
                  {
                    fieldPath: 'citizenshipDetails.firstEntryDate',
                    id: 'first-entry-date',
                    label: 'First Entry Date',
                    value: formatDate(citizenship?.firstEntryDate),
                  },
                  {
                    fieldPath: 'citizenshipDetails.entryVisaType',
                    id: 'entry-visa-type',
                    label: 'Visa Type at First Entry',
                    value: citizenship?.entryVisaType ?? 'N/A',
                  },
                  {
                    fieldPath: 'citizenshipDetails.yearsInUSPast5Years',
                    id: 'years-in-us',
                    label: 'Years in the U.S. of the Past Five (5) Years',
                    value: yearsInUsLabel(citizenship?.yearsInUSPast5Years),
                  },
                  {
                    fieldPath: 'citizenshipDetails.immigrationData.0.immigrationDate',
                    id: 'immigration-date',
                    label: 'Immigration Date',
                    value: formatDate(immigration?.immigrationDate),
                  },
                  {
                    fieldPath: 'citizenshipDetails.immigrationData.0.visaNumber',
                    id: 'visa-number',
                    label: 'Visa Number',
                    value: immigration?.visaNumber ?? 'N/A',
                  },
                ]
              : []),
            {
              fieldPath: 'citizenshipDetails.passport.number',
              id: 'passport-number',
              label: 'Passport Number',
              value: citizenship?.passport?.number ?? 'N/A',
            },
            {
              fieldPath: 'citizenshipDetails.passport.type',
              id: 'passport-type',
              label: 'Passport Type',
              value: citizenship?.passport?.type ?? 'N/A',
            },
            {
              fieldPath: 'citizenshipDetails.passport.issuanceCountry',
              id: 'passport-issuance-country',
              label: 'Passport Issuance Country',
              value: citizenship?.passport?.issuanceCountry ?? 'N/A',
            },
            {
              fieldPath: 'citizenshipDetails.passport.issueDate',
              id: 'passport-issue-date',
              label: 'Passport Issuance Date',
              value: formatDate(citizenship?.passport?.issueDate),
            },
            {
              fieldPath: 'citizenshipDetails.passport.expirationDate',
              id: 'passport-expiration-date',
              label: 'Passport Expiration Date',
              value: formatDate(citizenship?.passport?.expirationDate),
            },
          ]
        : []),
    ],
    title: 'My Citizenship Details',
  };
}

function buildContractSection(
  contractDetails?: ContractDetails,
  contract?: ApplicationWithUser['contract'],
  laborCategory?: ApplicationWithUser['laborCategory'],
): ApprovalDetailsSection {
  const addr = contractDetails?.workAddress;

  return {
    id: SectionId.CONTRACT_DETAILS,
    rows: [
      { id: 'contract-number', isEditable: false, label: 'Contract Number', value: contract?.contractNumber ?? 'N/A' },
      { id: 'company', isEditable: false, label: 'Company', value: contract?.companyName ?? 'N/A' },
      { id: 'labor-category', isEditable: false, label: 'Labor Category', value: laborCategory?.categoryName ?? 'N/A' },
      {
        fieldPath: 'contractDetails.cmsRegionFacility',
        id: 'cms-region-facility',
        label: 'CMS Region/Facility',
        value: contractDetails?.cmsRegionFacility ?? 'N/A',
      },
      {
        fieldPath: 'contractDetails.accessJustification',
        id: 'request-justification',
        label: 'Please provide details for why you need access to CMS systems and/or infrastructure.',
        value: contractDetails?.accessJustification ?? 'N/A',
      },
      {
        fieldPath: 'contractDetails.workEmail',
        id: 'work-email',
        label: 'Work Email',
        value: contractDetails?.workEmail ?? 'N/A',
      },
      {
        fieldPath: 'contractDetails.workPhoneNumber',
        id: 'work-phone-number',
        label: 'Work Phone Number',
        value: contractDetails?.workPhoneNumber ?? 'N/A',
      },
      {
        fieldPath: 'contractDetails.workExtension',
        id: 'work-extension',
        label: 'Work Extension',
        value: contractDetails?.workExtension ?? 'N/A',
      },
      {
        fieldPath: 'contractDetails.workAddress.streetLine1',
        id: 'work-address-line-1',
        label: 'Work Address Line 1',
        value: addr?.streetLine1 ?? 'N/A',
      },
      {
        fieldPath: 'contractDetails.workAddress.streetLine2',
        id: 'work-address-line-2',
        label: 'Work Address Line 2',
        value: addr?.streetLine2 ?? 'N/A',
      },
      {
        fieldPath: 'contractDetails.workAddress.city',
        id: 'work-address-city',
        label: 'Work Address City',
        value: addr?.city ?? 'N/A',
      },
      {
        fieldPath: 'contractDetails.workAddress.state',
        id: 'work-address-state',
        label: 'Work Address State',
        value: addr?.state ?? 'N/A',
      },
      {
        fieldPath: 'contractDetails.workAddress.zipCode',
        id: 'work-address-zip-code',
        label: 'Work Address Zip Code',
        value: addr?.zipCode ?? 'N/A',
      },
    ],
    title: 'Contract Details',
  };
}

function docRefToFile(ref: DocumentRef, documentFieldName: DocumentFieldName): DocumentFile {
  return {
    documentFieldPath: `documents.${documentFieldName}`,
    fileName: ref.filename ?? '',
    fileSizeLabel: formatFileSize(ref.fileSize),
    fileUrl: ref.fileUrl ?? '',
    id: ref.id,
    mimeType: ref.mimeType,
  };
}

function buildDocumentUploadSection(documents?: Documents, citizenship?: CitizenshipDetails): ApprovalDetailsSection {
  const legalAndOtherDocuments = [
    ...(documents?.otherDocuments?.map((document) => docRefToFile(document, DocumentFieldName.OTHER_DOCUMENTS)) ?? []),
    ...(documents?.legalDocuments?.map((document) => docRefToFile(document, DocumentFieldName.LEGAL_DOCUMENTS)) ?? []),
  ];

  const documentGroups = [
    documents?.pivTrainingCertificate && {
      documents: [docRefToFile(documents.pivTrainingCertificate, DocumentFieldName.PIV_TRAINING_CERTIFICATE)],
      id: DocumentGroupId.PIV_TRAINING_CERTIFICATE,
      title: 'PIV Training Certificate',
    },
    documents?.resume && {
      documents: [docRefToFile(documents.resume, DocumentFieldName.RESUME)],
      id: DocumentGroupId.RESUME,
      title: 'Resume',
    },
    citizenship?.isUsCitizen === false &&
      documents?.passport && {
        documents: [docRefToFile(documents.passport, DocumentFieldName.PASSPORT)],
        id: DocumentGroupId.PASSPORT,
        title: 'Passport',
      },
    citizenship?.isUsCitizen === false &&
      (documents?.usVisaDocument ||
        documents?.residencyDocument ||
        documents?.residencySupportingDocuments?.length) && {
        documents: [
          ...(citizenship.isPermanentResident === false && documents.usVisaDocument
            ? [docRefToFile(documents.usVisaDocument, DocumentFieldName.US_VISA_DOCUMENT)]
            : []),
          ...(citizenship.isPermanentResident === true && documents.residencyDocument
            ? [docRefToFile(documents.residencyDocument, DocumentFieldName.RESIDENCY_DOCUMENT)]
            : []),
          ...(documents.residencySupportingDocuments?.map((document) =>
            docRefToFile(document, DocumentFieldName.RESIDENCY_SUPPORTING_DOCUMENTS),
          ) ?? []),
        ],
        id: DocumentGroupId.RESIDENCY_DOCUMENT,
        title: 'Residency Documents',
      },
    legalAndOtherDocuments.length > 0 && {
      documents: legalAndOtherDocuments,
      id: DocumentGroupId.LEGAL_AND_OTHER_DOCUMENTS,
      title: 'Legal and Other Documents',
    },
  ].filter((group): group is DocumentGroup => Boolean(group));

  return {
    documentGroups: documentGroups.length > 0 ? documentGroups : undefined,
    id: SectionId.DOCUMENT_UPLOAD,
    rows: [],
    title: 'Document Upload',
  };
}

function buildForeignNationalInformationSection(): ApprovalDetailsSection {
  return {
    editable: false,
    id: SectionId.FOREIGN_NATIONAL_INFORMATION,
    rows: [],
    title: 'Foreign National Information',
  };
}

function buildAdditionalAccessInformationSection(
  applicationData?: ApplicationData,
  contract?: ApplicationWithUser['contract'],
): ApprovalDetailsAdditionalAccessSection {
  // ACP-2438: a contract that does not permit temporary or seasonal users never gets
  // asked the six-month question, and the answer is No. Settling it here rather than
  // in the component keeps the approve handler from reading an unasked question as
  // unanswered and refusing to submit.
  const allowsTemporarySeasonal = contract?.hasTemporarySeasonalUsers === true;

  const hasExternalFederalPiv =
    applicationData?.hasExternalFederalPiv ?? applicationData?.personalDetails?.hasExternalFederalPiv ?? false;

  // corApproval is only ever written by a first COR approval, so a credential decision
  // here marks a re-approval pass (e.g. after a VAS return). Values outside the selectable
  // set (legacy 'external_nonfederal_piv') fall back to the first-pass behavior.
  const persistedCredentialType = applicationData?.corApproval?.credentialType;
  const persistedPivCardSelection: PivCardSelection | null =
    CREDENTIAL_TYPES.find((credentialType) => credentialType === persistedCredentialType) ?? null;

  return {
    additionalAccessData: {
      allowsTemporarySeasonal,
      hasExternalFederalPiv,
      persistedPivCardSelection,
      pivCardSelection: persistedPivCardSelection ?? (hasExternalFederalPiv ? 'external_federal_piv' : null),
      workingSixMonths: allowsTemporarySeasonal ? applicationData?.contractDetails?.seasonal ?? null : false,
    },
    editable: false,
    id: SectionId.ADDITIONAL_ACCESS_INFORMATION,
    rows: [],
    title: 'Additional Access Information',
  };
}

export function mapApplicationToSections(app: ApplicationWithUser): ApprovalDetailsSection[] {
  const { applicationData, user, contract, laborCategory } = app;
  const isForeignNational = applicationData?.citizenshipDetails?.isUsCitizen === false;

  return [
    buildPersonalDetailsSection(user, applicationData?.personalDetails),
    buildContractSection(applicationData?.contractDetails, contract, laborCategory),
    buildCitizenshipSection(applicationData?.citizenshipDetails, isForeignNational),
    buildDocumentUploadSection(applicationData?.documents, applicationData?.citizenshipDetails),
    buildAdditionalAccessInformationSection(applicationData, contract),
    ...(isForeignNational ? [buildForeignNationalInformationSection()] : []),
  ];
}
